import { useEffect } from 'react';
import { X, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import { useDeleteUserMutation } from '@/features/user';
import { toast } from 'sonner';

interface DeleteUserConfirmModalProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly user: {
    userId: string;
    fullname: string;
    nrp: string;
  } | null;
}

export function DeleteUserConfirmModal({
  isOpen,
  onClose,
  user,
}: DeleteUserConfirmModalProps) {
  const deleteUserMutation = useDeleteUserMutation();

  const handleConfirm = async () => {
    if (!user) return;

    try {
      await deleteUserMutation.mutateAsync(user.userId);
      toast.success('User deleted successfully');
      onClose();
    } catch (error: any) {
      console.error('[User] Delete failed:', error);
      toast.error(error?.message || 'Failed to delete user');
    }
  };

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen || !user) return null;

  return (
    <>
      {/* Backdrop */}
      <button
        type="button"
        className="fixed inset-0 bg-black/50 z-40 border-0 cursor-default"
        onClick={onClose}
        aria-label="Close modal"
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none">
        <div className="bg-slate-900 rounded-lg border border-slate-700 shadow-2xl w-full max-w-md mx-4 pointer-events-auto">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700">
            <h2 className="text-lg font-semibold text-white">Delete User</h2>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="text-slate-400 hover:text-white p-1 h-auto"
              aria-label="Close modal"
              disabled={deleteUserMutation.isPending}
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Content */}
          <div className="px-6 py-6 space-y-4">
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Are you sure?</AlertTitle>
              <AlertDescription>
                This action cannot be undone. This will permanently delete the
                user account for <strong>{user.fullname}</strong> (NRP:{' '}
                <strong>{user.nrp}</strong>).
              </AlertDescription>
            </Alert>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-700">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={deleteUserMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleConfirm}
              disabled={deleteUserMutation.isPending}
              className={cn(
                deleteUserMutation.isPending && 'opacity-80 cursor-not-allowed'
              )}
            >
              {deleteUserMutation.isPending ? 'Deleting...' : 'Yes, Delete'}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
